/**
 * InputManager - Minimal Essential Version for ClientFirstDocumentEditor.tsx
 * 
 * Handles keyboard and mouse input for document editing within the ClientFirst architecture.
 * Stripped down from 858 lines to essentials - just event capture and delegation.
 * 
 * Architecture Integration:
 * - Used by ClientFirstDocumentEditor.tsx for all user input handling
 * - Coordinates with the active rendering pipeline for immediate visual feedback
 * - Integrates with SyncManager for server-authoritative layout reconciliation
 * - Manages CaretManager, NavigationEngine, and SelectionManager
 * 
 * Note: Currently commented out in ClientFirstDocumentEditor.tsx for pure server layout testing,
 * but designed for full integration with the ClientFirst document editing pipeline.
 */

import { EventEmitter } from 'events';
import { LocalDocumentEngine } from '../core/LocalDocumentEngine';
import { SimplifiedCaretSystem } from '../caret/SimplifiedCaretSystem';
import { NavigationEngine } from './NavigationEngine';
import { SelectionManager } from './SelectionManager';

// Minimal renderer interface for input management
interface MinimalRenderer {
  getCanvas(): HTMLCanvasElement;
  getContext(): CanvasRenderingContext2D;
  requestRender(): void;
  mapPointToTextIndex?(x: number, y: number): number;
  setDocumentState?(state: any): void;
}

export class InputManager extends EventEmitter {
  private documentEngine!: LocalDocumentEngine;
  private renderer!: MinimalRenderer;
  private caretSystem!: SimplifiedCaretSystem;
  private navigationEngine!: NavigationEngine;
  private selectionManager!: SelectionManager;
  private canvas!: HTMLCanvasElement;
  
  private isEnabled: boolean = true;
  private readOnly: boolean = false;
  
  // Formatting storage for rich text paste
  private documentFormatting: Map<number, any[]> = new Map(); // position -> styles

  private renderTimeout: number | null = null;
  
  // Async rendering queue for performance
  private renderQueue: Array<() => void> = [];
  private isProcessingRenderQueue: boolean = false;
  private lastRenderTime: number = 0;
  private renderThrottleMs: number = 16; // ~60fps max
  
  // Event handler references for proper cleanup
  private boundHandlers: {
    handleKeyDown: (e: KeyboardEvent) => void;
    handleKeyUp: (e: KeyboardEvent) => void;
    handleKeyPress: (e: KeyboardEvent) => void;
    handleMouseDown: (e: MouseEvent) => void;
    handleClick: (e: MouseEvent) => void;
    handleCopy: (e: ClipboardEvent) => void;
    handlePaste: (e: ClipboardEvent) => void;
    handleFocus: () => void;
    handleBlur: () => void;
  } | null = null;

  constructor() {
    super();
    // Empty constructor - initialization happens in initialize()
  }

  /**
   * Initialize the InputManager with required dependencies
   */
  async initialize(
    canvas: HTMLCanvasElement,
    documentEngine: LocalDocumentEngine,
    renderer: MinimalRenderer,
    collaborationBridge?: any,
    caretSystem?: SimplifiedCaretSystem
  ): Promise<void> {
    this.canvas = canvas;
    this.documentEngine = documentEngine;
    this.renderer = renderer;
    
    // Use provided SimplifiedCaretSystem (from PaperCanvas) or create fallback
    if (caretSystem) {
      this.caretSystem = caretSystem;
    } else {
      // Fallback: create a minimal system for InputManager-only mode
      this.caretSystem = new SimplifiedCaretSystem({
        enableHighDPI: true,
        enableSubPixelRendering: true,
        enableAnimations: true,
        enableContextAwareStyling: true,
        enableBlinking: true,
        debugMode: false
      });
    }
    
    // Create navigation engine with SimplifiedCaretSystem
    this.navigationEngine = new NavigationEngine(documentEngine, this.caretSystem);
    this.selectionManager = new SelectionManager(documentEngine, renderer);
    
    this.setupEventListeners();
  }

  /**
   * Set up event listeners with proper references for cleanup
   */
  private setupEventListeners(): void {
    // Create bound handler references once
    this.boundHandlers = {
      handleKeyDown: (e: KeyboardEvent) => this.handleKeyDown(e),
      handleKeyUp: (e: KeyboardEvent) => this.handleKeyUp(e),
      handleKeyPress: (e: KeyboardEvent) => this.handleKeyPress(e),
      handleMouseDown: (e: MouseEvent) => this.handleMouseDown(e),
      handleClick: (e: MouseEvent) => this.handleClick(e).catch(console.error),
      handleCopy: (e: ClipboardEvent) => this.handleCopy(e),
      handlePaste: (e: ClipboardEvent) => this.handlePaste(e),
      handleFocus: () => this.handleFocus(),
      handleBlur: () => this.handleBlur()
    };
    
    // Add event listeners using bound references
    this.canvas.addEventListener('keydown', this.boundHandlers.handleKeyDown);
    this.canvas.addEventListener('keyup', this.boundHandlers.handleKeyUp);
    this.canvas.addEventListener('keypress', this.boundHandlers.handleKeyPress);
    this.canvas.addEventListener('mousedown', this.boundHandlers.handleMouseDown);
    this.canvas.addEventListener('click', this.boundHandlers.handleClick);
    this.canvas.addEventListener('copy', this.boundHandlers.handleCopy);
    this.canvas.addEventListener('paste', this.boundHandlers.handlePaste);
    this.canvas.addEventListener('focus', this.boundHandlers.handleFocus);
    this.canvas.addEventListener('blur', this.boundHandlers.handleBlur);
    
    console.log('[InputManager] Event listeners attached with proper references');
  }

  /**
   * Handle keydown events
   */
  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.isEnabled) return;

    // Key press logging removed for performance

    // Handle navigation keys (arrows, home, end, etc.)
    if (this.isNavigationKey(e)) {
      e.preventDefault(); // Prevent browser default (scrolling)
      this.handleNavigationKey(e);
      this.updateRenderer();
      return;
    }

    // DISABLE text editing to prevent duplicate layout requests with PaperCanvas
    // PaperCanvas handles all text input/editing operations and triggers backend layout
    // InputManager now focuses on navigation and selection only
    if (this.isTextEditingKey(e)) {
      // Let PaperCanvas handle text editing - don't prevent default or process
      // This eliminates duplicate backend layout requests while preserving InputManager's
      // navigation and selection capabilities
      return;
    }
  }

  /**
   * Handle navigation keys (arrows, home, end, page up/down)
   */
  private handleNavigationKey(e: KeyboardEvent): void {
    // Navigation logging removed for performance
    
    // Start selection if Shift is pressed
    if (e.shiftKey && !this.selectionManager.hasSelection()) {
      this.selectionManager.startSelection(this.documentEngine.getCaretPosition());
    }

    // Handle navigation
    switch (e.key) {
      case 'ArrowLeft':
        this.navigationEngine.moveLeft(e.ctrlKey);
        break;
      case 'ArrowRight':
        this.navigationEngine.moveRight(e.ctrlKey);
        break;
      case 'ArrowUp':
        this.navigationEngine.moveUp();
        break;
      case 'ArrowDown':
        this.navigationEngine.moveDown();
        break;
      case 'Home':
        this.navigationEngine.moveToLineStart(e.ctrlKey);
        break;
      case 'End':
        this.navigationEngine.moveToLineEnd(e.ctrlKey);
        break;
    }

    // Update selection if Shift was pressed
    if (e.shiftKey) {
      this.selectionManager.extendSelection(this.documentEngine.getCaretPosition());
    } else {
      this.selectionManager.clearSelection();
    }
  }

  /**
   * Handle text editing keys (letters, numbers, backspace, delete, enter)
   */
  private handleTextEditingKey(e: KeyboardEvent): void {
    const caretPosition = this.documentEngine.getCaretPosition();

    switch (e.key) {
      case 'Backspace':
        // Delete selection first, or single character if no selection
        if (this.selectionManager.hasSelection()) {
          this.deleteSelection();
        } else if (caretPosition > 0) {
          // Let deleteRange handle caret positioning internally
          this.documentEngine.deleteRange(caretPosition - 1, caretPosition);
        }
        break;
        
      case 'Delete':
        // Delete selection first, or single character if no selection
        if (this.selectionManager.hasSelection()) {
          this.deleteSelection();
        } else {
          const textLength = this.documentEngine.getText().length;
          if (caretPosition < textLength) {
            // Let deleteRange handle caret positioning internally
            this.documentEngine.deleteRange(caretPosition, caretPosition + 1);
          }
        }
        break;
        
      case 'Enter':
        // Delete selection first if exists, then insert paragraph break
        if (this.selectionManager.hasSelection()) {
          this.deleteSelection();
        }
        this.documentEngine.insertParagraphBreak(this.documentEngine.getCaretPosition());
        break;
        
      default:
        // Regular character input
        if (e.key.length === 1) {
          // Delete selection first if exists, then insert character
          if (this.selectionManager.hasSelection()) {
            this.deleteSelection();
          }
          this.documentEngine.insertCharacter(this.documentEngine.getCaretPosition(), e.key);
        }
        break;
    }
  }

  /**
   * Delete the current selection
   */
  private deleteSelection(): void {
    const selection = this.selectionManager.getSelection();
    if (selection) {
      this.documentEngine.deleteRange(selection.start, selection.end);
      this.selectionManager.clearSelection();
    }
  }

  /**
   * Handle mouse clicks - position caret using SimplifiedCaretSystem
   */
  private async handleClick(e: MouseEvent): Promise<void> {
    if (!this.isEnabled) return;

    const rect = this.canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    
    try {
      // Use SimplifiedCaretSystem for precise positioning
      const position = await this.caretSystem.positionFromClick(canvasX, canvasY);
      const textPosition = position.index;
      
      this.documentEngine.moveCaretTo(textPosition);
      
      // Get the current layout plan to ensure consistency
      const currentLayoutPlan = this.caretSystem.getCurrentLayoutPlan?.();
      
      // Update caret system with new position using the same layout plan
      await this.caretSystem.updateCaretPosition(textPosition, currentLayoutPlan);
      
      this.selectionManager.clearSelection();
      this.updateRenderer();
    } catch (error) {
      console.warn('[InputManager] Caret positioning failed, using fallback:', error);
      // Fallback to renderer method
      const textPosition = this.renderer.mapPointToTextIndex?.(canvasX, canvasY) ?? 0;
      this.documentEngine.moveCaretTo(textPosition);
      this.selectionManager.clearSelection();
      this.updateRenderer();
    }
  }

  /**
   * Handle mouse down - start potential selection
   */
  private handleMouseDown(e: MouseEvent): void {
    if (!this.isEnabled) return;
    
    // Focus the canvas
    this.canvas.focus();
  }

  /**
   * Handle copy
   */
  private handleCopy(e: ClipboardEvent): void {
    if (this.selectionManager.hasSelection()) {
      const selectedText = this.selectionManager.getSelectedText();
      e.clipboardData?.setData('text/plain', selectedText);
      e.preventDefault();
    }
  }

  /**
   * Handle paste - supports both plain text and rich HTML formatting
   */
  private handlePaste(e: ClipboardEvent): void {
    if (this.readOnly) {
      e.preventDefault();
      return;
    }

    // Try to get HTML content first, fallback to plain text
    const htmlContent = e.clipboardData?.getData('text/html') || '';
    const plainText = e.clipboardData?.getData('text/plain') || '';
    
    if (htmlContent) {
      console.log(`📋 [InputManager] Rich HTML paste: ${htmlContent.length} characters`);
      this.handleRichTextPaste(htmlContent);
    } else if (plainText) {
      console.log(`📋 [InputManager] Plain text paste: ${plainText.length} characters`);
      this.handlePlainTextPaste(plainText);
    }
    
    e.preventDefault();
  }

  /**
   * Handle rich HTML paste with formatting preservation
   */
  private handleRichTextPaste(htmlContent: string): void {
    try {
      // Parse HTML to extract text and formatting
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      
      // Extract formatted text with style information
      const formattedContent = this.extractFormattedText(doc.body);
      
      // Delete selection first if exists
      if (this.selectionManager.hasSelection()) {
        this.deleteSelection();
      }
      
      // Insert formatted content
      const caretPosition = this.documentEngine.getCaretPosition();
      this.documentEngine.insertBulkText(caretPosition, formattedContent.text);
      
      // Store formatting information for backend processing
      this.storeFormattingInfo(formattedContent.styles, caretPosition);
      
      // Single render update after paste completes
      this.updateRenderer();
      
    } catch (error) {
      console.error('[InputManager] Rich text paste failed:', error);
      // Fallback to plain text extraction from HTML
      const plainText = this.extractPlainTextFromHTML(htmlContent);
      if (plainText) {
        this.handlePlainTextPaste(plainText);
      }
    }
  }

  /**
   * Handle plain text paste (existing functionality)
   */
  private handlePlainTextPaste(plainText: string): void {
    // Delete selection first if exists
    if (this.selectionManager.hasSelection()) {
      this.deleteSelection();
    }
    
    // Insert entire paste as one bulk operation
    const caretPosition = this.documentEngine.getCaretPosition();
    this.documentEngine.insertBulkText(caretPosition, plainText);
    
    // Single render update after paste completes
    this.updateRenderer();
  }

  /**
   * Extract formatted text from HTML DOM
   */
  private extractFormattedText(element: Element): { text: string; styles: any[] } {
    const styles: any[] = [];
    let text = '';
    let currentIndex = 0;
    
    const processNode = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textContent = node.textContent || '';
        text += textContent;
        currentIndex += textContent.length;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();
        
        // Extract formatting information
        const style: any = {
          start: currentIndex,
          end: currentIndex,
          tag: tagName
        };
        
        // Extract CSS styles
        const computedStyle = window.getComputedStyle(element);
        if (computedStyle.fontWeight === 'bold' || tagName === 'b' || tagName === 'strong') {
          style.bold = true;
        }
        if (computedStyle.fontStyle === 'italic' || tagName === 'i' || tagName === 'em') {
          style.italic = true;
        }
        if (computedStyle.textDecoration?.includes('underline') || tagName === 'u') {
          style.underline = true;
        }
        if (computedStyle.color && computedStyle.color !== 'rgb(0, 0, 0)') {
          style.color = computedStyle.color;
        }
        if (computedStyle.fontSize) {
          style.fontSize = computedStyle.fontSize;
        }
        if (computedStyle.fontFamily) {
          style.fontFamily = computedStyle.fontFamily;
        }
        
        // Process child nodes
        const startIndex = currentIndex;
        for (const child of Array.from(node.childNodes)) {
          processNode(child);
        }
        
        style.end = currentIndex;
        if (style.end > style.start) {
          styles.push(style);
        }
      }
    };
    
    processNode(element);
    
    return { text, styles };
  }

  /**
   * Extract plain text from HTML as fallback
   */
  private extractPlainTextFromHTML(htmlContent: string): string {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      return doc.body?.textContent || '';
    } catch (error) {
      console.error('[InputManager] Plain text extraction failed:', error);
      return '';
    }
  }

  /**
   * Store formatting information for backend processing
   */
  private storeFormattingInfo(styles: any[], insertPosition: number): void {
    if (styles.length > 0) {
      // Adjust style positions to account for insertion point
      const adjustedStyles = styles.map(style => ({
        ...style,
        start: style.start + insertPosition,
        end: style.end + insertPosition
      }));
      
      this.documentFormatting.set(insertPosition, adjustedStyles);
      console.log(`📋 [InputManager] Stored ${adjustedStyles.length} formatting styles at position ${insertPosition}`);
      
      // Emit formatting update event
      this.emit('formatting-updated', adjustedStyles);
    }
  }

  /**
   * Get formatting information for a document position
   */
  public getFormattingInfo(position: number): any[] {
    for (const [insertPos, styles] of this.documentFormatting.entries()) {
      if (position >= insertPos) {
        return styles.filter(style => position >= style.start && position < style.end);
      }
    }
    return [];
  }

  /**
   * Clear formatting information (call when document is cleared)
   */
  public clearFormattingInfo(): void {
    this.documentFormatting.clear();
  }

  /**
   * Handle keyup events
   */
  private handleKeyUp(e: KeyboardEvent): void {
    // Key release logging removed for performance
    // Key release handling - browser automatically stops repeat events
  }

  /**
   * Handle keypress (mostly for composition)
   */
  private handleKeyPress(e: KeyboardEvent): void {
    // Most handling is done in keydown
  }

  /**
   * Handle focus
   */
  private handleFocus(): void {
    console.log('[InputManager] Canvas focused');
  }

  /**
   * Handle blur
   */
  private handleBlur(): void {
    console.log('[InputManager] Canvas blurred');
  }

  /**
   * Update renderer with current document state - immediate for reliability
   */
  private updateRenderer(): void {
    // Clear any pending render frame
    if (this.renderTimeout) {
      cancelAnimationFrame(this.renderTimeout);
    }
    
    // Use requestAnimationFrame for smooth rendering
    this.renderTimeout = requestAnimationFrame(async () => {
      const state = {
        text: this.documentEngine.getText(),
        caretPosition: this.documentEngine.getCaretPosition(),
        selection: this.selectionManager.getSelection()
      };
      
      // Update caret system with new position
      try {
        const currentLayoutPlan = this.caretSystem.getCurrentLayoutPlan?.();
        await this.caretSystem.updateCaretPosition(state.caretPosition, currentLayoutPlan);
      } catch (error) {
        console.warn('[InputManager] Failed to update caret position:', error);
      }
      
      this.renderer.setDocumentState?.(state);
      this.renderer.requestRender();
      this.renderTimeout = null;
    });
  }

  /**
   * Queue a render operation for async processing
   */
  private queueRender(renderFn: () => void): void {
    // Replace any existing render in queue (only keep latest)
    this.renderQueue.length = 0;
    this.renderQueue.push(renderFn);
    
    if (!this.isProcessingRenderQueue) {
      this.processRenderQueue();
    }
  }

  /**
   * Process render queue asynchronously
   */
  private async processRenderQueue(): Promise<void> {
    if (this.isProcessingRenderQueue) return;
    
    this.isProcessingRenderQueue = true;
    
    while (this.renderQueue.length > 0) {
      const now = performance.now();
      
      // Throttle rendering to maintain 60fps max
      if (now - this.lastRenderTime < this.renderThrottleMs) {
        await new Promise(resolve => setTimeout(resolve, this.renderThrottleMs - (now - this.lastRenderTime)));
      }
      
      const renderFn = this.renderQueue.shift();
      if (renderFn) {
        const renderStart = performance.now();
        renderFn();
        const renderTime = performance.now() - renderStart;
        
        console.log(`🎯 [InputManager] Async render completed in ${renderTime.toFixed(2)}ms`);
        this.lastRenderTime = performance.now();
      }
    }
    
    this.isProcessingRenderQueue = false;
  }

  /**
   * Check if key is a navigation key
   */
  private isNavigationKey(e: KeyboardEvent): boolean {
    return [
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'PageUp', 'PageDown'
    ].includes(e.key);
  }

  /**
   * Check if key is a text editing key
   */
  private isTextEditingKey(e: KeyboardEvent): boolean {
    return (
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'Enter' ||
      (e.key.length === 1 && !e.ctrlKey && !e.metaKey)
    );
  }

  // Public interface methods
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  setReadOnly(readOnly: boolean): void {
    this.readOnly = readOnly;
  }

  focus(): void {
    this.canvas.focus();
  }

  blur(): void {
    this.canvas.blur();
  }

  // Get the caret system for external use
  getCaretSystem(): SimplifiedCaretSystem {
    return this.caretSystem;
  }

  destroy(): void {
    // Remove event listeners using proper references
    if (this.boundHandlers && this.canvas) {
      this.canvas.removeEventListener('keydown', this.boundHandlers.handleKeyDown);
      this.canvas.removeEventListener('keyup', this.boundHandlers.handleKeyUp);
      this.canvas.removeEventListener('keypress', this.boundHandlers.handleKeyPress);
      this.canvas.removeEventListener('mousedown', this.boundHandlers.handleMouseDown);
      this.canvas.removeEventListener('click', this.boundHandlers.handleClick);
      this.canvas.removeEventListener('copy', this.boundHandlers.handleCopy);
      this.canvas.removeEventListener('paste', this.boundHandlers.handlePaste);
      this.canvas.removeEventListener('focus', this.boundHandlers.handleFocus);
      this.canvas.removeEventListener('blur', this.boundHandlers.handleBlur);
      
      console.log('[InputManager] Event listeners properly removed');
      this.boundHandlers = null;
    }
    
    // Clean up caret system
    if (this.caretSystem) {
      this.caretSystem.destroy();
    }
    
    // Clean up render queue
    this.renderQueue.length = 0;
    this.isProcessingRenderQueue = false;
    
    if (this.renderTimeout) {
      cancelAnimationFrame(this.renderTimeout);
      this.renderTimeout = null;
    }
  }

  // Stub methods for interface compatibility
  startInputProcessing(): void {}
  stopInputProcessing(): void {}
  isReadOnly(): boolean { return this.readOnly; }
  isFocused(): boolean { return document.activeElement === this.canvas; }
  getPerformanceMetrics(): any { return {}; }
}
